import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'bokr_auth_token';

// Create axios instance
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Helper to get stored token
export const getStoredToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

// Helper to store token
export const storeToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    console.log('Token stored in localStorage');
  }
};

// Helper to clear token
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  console.log('Token cleared from localStorage');
};

// Add request interceptor to include token (if available)
api.interceptors.request.use(
  (config) => {
    // Try to add token from localStorage if we have one
    // The backend will also check the _ms-mid cookie as a fallback
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // withCredentials: true is already set, so the _ms-mid cookie 
    // will be sent automatically by the browser
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('Authentication error for:', error.config?.url);
      
      // Dispatch custom event to trigger logout
      // This is caught by the auth context to clear session
      window.dispatchEvent(new CustomEvent('auth:unauthorized', { 
        detail: { url: error.config?.url }
      }));
    }
    return Promise.reject(error);
  }
);

// Services
export const getServices = () => api.get('/services').then(r => r.data);
export const createService = (data) => api.post('/services', data).then(r => r.data);
export const updateService = (id, data) => api.put(`/services/${id}`, data).then(r => r.data);

// Clients
export const getClients = (params) => api.get('/clients', { params }).then(r => r.data);
export const getClient = (id) => api.get(`/clients/${id}`).then(r => r.data);
export const createClient = (data) => api.post('/clients', data).then(r => r.data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data).then(r => r.data);
export const toggleClientActive = (id, force = false) => api.patch(`/clients/${id}/toggle-active`, null, { params: { force } }).then(r => r.data);
export const getClientLedger = (id) => api.get(`/clients/${id}/ledger`).then(r => r.data);
export const getClientTransactions = (id, params) => api.get(`/clients/${id}/transactions`, { params }).then(r => r.data);
export const updateBusinessSettings = (data) => api.put('/settings/business', data).then(r => r.data);
export const getClientFutureBookings = (id) => api.get(`/clients/${id}/future-bookings`).then(r => r.data);

// Bookings
export const getBookings = (params) => api.get('/bookings', { params }).then(r => r.data);
export const createBooking = (data) => api.post('/bookings', data).then(r => r.data);
export const updateBooking = (id, data) => api.put(`/bookings/${id}`, data).then(r => r.data);
export const updateBookingStatus = (id, status) => api.patch(`/bookings/${id}/status`, { status }).then(r => r.data);
export const cancelBooking = (id, data) => api.post(`/bookings/${id}/cancel`, data).then(r => r.data);
export const rescheduleBooking = (id, data) => api.post(`/bookings/${id}/reschedule`, data).then(r => r.data);
export const reconcileBooking = (id) => api.patch(`/bookings/${id}/reconcile`).then(r => r.data);
export const reinstateBooking = (id) => api.post(`/bookings/${id}/reinstate`).then(r => r.data);
export const deleteBooking = (id) => api.delete(`/bookings/${id}`).then(r => r.data);
export const transferBooking = (id, targetClientId) => api.post(`/bookings/${id}/transfer`, null, { params: { target_client_id: targetClientId } }).then(r => r.data);
export const getUnbilledBookings = () => api.get('/bookings/unbilled').then(r => r.data);
export const getBookingsLifecycle = () => api.get('/bookings/lifecycle').then(r => r.data);

// Invoices
export const getInvoices = () => api.get('/invoices').then(r => r.data);
export const getInvoice = (id) => api.get(`/invoices/${id}`).then(r => r.data);
export const createInvoice = (data) => api.post('/invoices', data).then(r => r.data);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data).then(r => r.data);
export const createInvoiceFromBookings = (data) => api.post('/invoices/from-bookings', data).then(r => r.data);
export const sendInvoice = (id) => api.post(`/invoices/${id}/send`).then(r => r.data);

// Payments
export const getPayments = () => api.get('/payments').then(r => r.data);
export const createPayment = (data) => api.post('/payments', data).then(r => r.data);

// Credits
export const getCredits = () => api.get('/credits').then(r => r.data);
export const getClientCredits = (clientId) => api.get(`/credits/client/${clientId}`).then(r => r.data);
export const applyCredit = (data) => api.post('/credits/apply', data).then(r => r.data);

// Invoice Detail & Archive
export const getInvoiceDetail = (invoiceId) => api.get(`/invoices/${invoiceId}/detail`).then(r => r.data);
export const archivePaidInvoices = () => api.post('/invoices/archive').then(r => r.data);
export const archiveInvoice = (invoiceId) => api.post(`/invoices/${invoiceId}/archive`).then(r => r.data);
export const addBookingsToInvoice = (invoiceId, data) => api.post(`/invoices/${invoiceId}/add-bookings`, data).then(r => r.data);
export const getClientDraftInvoices = (clientId) => api.get(`/invoices/drafts/${clientId}`).then(r => r.data);

// Dashboard
export const getDashboardStats = (period) => api.get('/dashboard/stats', { params: { period } }).then(r => r.data);
export const getTopClients = (metric, limit) => api.get('/dashboard/top-clients', { params: { metric, limit } }).then(r => r.data);
export const getRevenueByService = () => api.get('/dashboard/revenue-by-service').then(r => r.data);

// Availability
export const getAvailability = () => api.get('/availability').then(r => r.data);
export const updateAvailability = (data) => api.put('/availability', data).then(r => r.data);
export const addBlockedTime = (data) => api.post('/availability/blocked-time', data).then(r => r.data);
export const deleteBlockedTime = (id) => api.delete(`/availability/blocked-time/${id}`).then(r => r.data);

// Exports
export const exportInvoices = () => `${API}/exports/invoices.csv`;
export const exportPayments = () => `${API}/exports/payments.csv`;
export const exportTransactions = () => `${API}/exports/transactions.csv`;

// Invoice PDF
export const getInvoicePdfUrl = (invoiceId) => `${API}/invoices/${invoiceId}/pdf`;

export default api;
